import React, { useState, useEffect } from 'react';
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  Layout,
  Modal,
  notification,
  Result,
  Row,
  Select,
  Skeleton,
  Switch,
  Tag,
  theme,
} from "antd";
import { useNavigate } from "react-router-dom";
import { EditOutlined } from "@ant-design/icons";
import {
  apiGetManagedAccounts,
  apiUpdateManagedAccount,
} from "../../../../api/virtualAccounts";
import "./ManagedAccountsList.scss";

const { Content } = Layout;
const { Search } = Input;

const ManagedAccountsList = () => {
  const [accounts, setAccounts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const fetchAccounts = async (params) => {
    setIsFetching(true);
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );
      const json = await apiGetManagedAccounts(filteredParams);
      setAccounts(json.results || []);
    } catch (error) {
      setActiveError(true);
      setErrorStatus(error.status);
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao buscar as contas gerenciadas.",
        placement: "topRight",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const onSearch = (value) => {
    setSearchText(value);
    fetchAccounts({
      search: value || undefined,
    });
  };

  const openEditModal = (record) => {
    setEditingAccount(record);
    form.setFieldsValue({
      name: record.name,
      automation_enabled: record.automation_enabled,
      notify_actions_to: record.notify_actions_to || [],
      notify_on_actions: record.notify_on_actions || [],
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsUpdating(true);
      await apiUpdateManagedAccount(editingAccount.id, values);
      notification.success({
        message: "Sucesso",
        description: "Conta gerenciada atualizada com sucesso.",
        placement: "topRight",
      });
      setEditModalOpen(false);
      setEditingAccount(null);
      form.resetFields();
      fetchAccounts({ search: searchText || undefined });
    } catch (error) {
      if (error.errorFields) return;
      notification.error({
        message: "Erro",
        description: "Ocorreu um erro ao atualizar a conta gerenciada.",
        placement: "topRight",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingAccount(null);
    form.resetFields();
  };

  useEffect(() => {
    fetchAccounts({});
  }, []);

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  return (
    <div className="ManagedAccountsList">
      <Layout style={{ padding: "1.25rem" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="title-page">Contas Virtuais</h2>
          </div>
          <Divider />
          <Breadcrumb.Item>Contas Gerenciadas</Breadcrumb.Item>
        </Breadcrumb>
        <Content
          style={{
            padding: 24,
            margin: 0,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <h3 className="subtitle-page">Contas Gerenciadas</h3>

          <div style={{ marginBottom: 16, marginTop: 16 }}>
            <Search
              placeholder="Buscar por nome da conta"
              allowClear
              enterButton="Buscar"
              onSearch={onSearch}
              style={{ width: 300 }}
            />
          </div>

          {isFetching ? (
            <Skeleton active />
          ) : activeError ? (
            renderError(errorStatus)
          ) : accounts.length === 0 ? (
            <Empty description="Sem dados" />
          ) : (
            <div className="account-cards-grid">
              <Row gutter={[24, 24]}>
                {accounts.map((record) => (
                  <Col xs={24} sm={12} lg={8} key={record.id}>
                    <Card
                      className="managed-account-card"
                      hoverable
                      title={record.account?.display_name}
                      extra={
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(record);
                          }}
                        />
                      }
                      onClick={() => navigate(`/conta-gerenciada/${record.id}`)}
                    >
                      <p><strong>{record.name}</strong></p>
                      <p>
                        <span className={`badge ${record.type === 'CORA' ? 'badge-cora' : 'badge-btg'}`}>{record.type}</span>
                      </p>
                      <p>
                        Automação:{" "}
                        {record.automation_enabled ? (
                          <Tag color="green">Ativada</Tag>
                        ) : (
                          <Tag color="red">Desativada</Tag>
                        )}
                      </p>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Content>
      </Layout>

      <Modal
        title="Editar Conta Gerenciada"
        open={editModalOpen}
        onOk={handleEditSubmit}
        onCancel={handleEditCancel}
        confirmLoading={isUpdating}
        okText="Salvar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Nome">
            <Input />
          </Form.Item>
          <Form.Item name="automation_enabled" label="Automação" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="notify_actions_to" label="Notificar ações para">
            <Select mode="tags" placeholder="Digite os e-mails" />
          </Form.Item>
          <Form.Item name="notify_on_actions" label="Notificar nas ações">
            <Select
              mode="multiple"
              placeholder="Selecione as ações"
              options={[
                { label: "Boleto", value: "BANK_SLIP" },
                { label: "Transferência", value: "TRANSFER" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManagedAccountsList;
